<script lang="ts">
  /**
   * Deduplicates concurrent loadRemoteEntry calls for the same scope.
   * Key includes full URL (with cache-bust params) so reload requests with ?t=... bypass dedup intentionally.
   */
  const loadingScripts = new Map<string, Promise<void>>();

  /**
   * Scopes THIS loader registered on a runtime instance (override / hot-reload
   * re-points). `registerRemotes` records permanently into the instance's
   * `options.remotes`, so without this ledger a loader-own registration would
   * satisfy the "host already knows the remote" gate — and a disabled override
   * would keep serving the stale registration instead of reverting to the
   * classic `window[scope]` container.
   */
  const loaderRegisteredRemotes = new WeakMap<object, Set<string>>();

  /** The same scopes across every instance, host or standalone. */
  const runtimeRegisteredScopes = new Set<string>();

  function markLoaderRegistered(instance: object, scope: string): void {
    let scopes = loaderRegisteredRemotes.get(instance);
    if (!scopes) {
      scopes = new Set();
      loaderRegisteredRemotes.set(instance, scopes);
    }
    scopes.add(scope);
    runtimeRegisteredScopes.add(scope);
  }

  function isLoaderRegistered(instance: object, scope: string): boolean {
    return loaderRegisteredRemotes.get(instance)?.has(scope) ?? false;
  }
</script>

<script setup lang="ts" generic="P extends ParcelPackage">
  import {
    ref,
    computed,
    inject,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick,
    type Ref,
  } from 'vue';
  import {
    MOUNT_PARCEL_KEY,
    PARCEL_REMOTES_KEY,
    CONTAINER_LOADED_EVENT,
    CONTAINER_CHANGED_EVENT,
    CONTAINER_INVALIDATED_EVENT,
    STATUS_CHANGED_EVENT,
    type FederationContainer,
    type ParcelConfig,
    type MountedParcel,
    type ContainerLifecycleDetail,
  } from '@platform/parcels/types';
  import { ParcelStatus, isParcelBroken } from '@platform/parcels/dev-tools/constants';
  import { getActiveOverridesPerScope, isDevMode } from '@platform/parcels/dev-tools';
  import {
    isLocalhostUrl,
    isValidDevEntryUrl,
    isValidEntryUrl,
  } from '@platform/parcels/dev-tools/url-presets';
  import { invalidateContainer } from '@platform/parcels/container-lifecycle';
  import {
    ensureRemoteReady,
    getHostFederationInstance,
    getStandaloneFederationInstance,
    instanceHasRemote,
  } from '@platform/remotes';
  import { rehomeCdnUrlToDocumentOrigin } from '@platform/parcels/cdn-origin';
  import { resolveLoadStrategy } from '@platform/parcels/consumer/load-strategy';
  import type { ParcelPackage, ParcelModule } from '@platform/index';

  // #region parcelloader-props
  const props = withDefaults(
    defineProps<{
      /** Remote scope name on window (e.g., '@abax/central/marketplace-ui') */
      package: P;
      /** Exposed module name (e.g., 'vehicle-installations') */
      module: ParcelModule<P>;
      /**
       * URL to the remoteEntry.js script that registers the container on window.
       * When omitted, assumes the container is already loaded (e.g. by the shell).
       * When provided, ParcelLoader loads the script before attempting to mount.
       */
      remoteUrl?: string;
      /** Props forwarded to the parcel */
      parcelProps?: Record<string, unknown>;
      /** Retry count on mount failure */
      maxRetries?: number;
      /** When true, suppresses fallback and error slots — parcel renders nothing on load/error */
      silent?: boolean;
      /** Timeout in ms for mount operation (default: 30000, 0 = no timeout) */
      mountTimeout?: number;
    }>(),
    {
      remoteUrl: undefined,
      parcelProps: () => ({}),
      maxRetries: 2,
      silent: false,
      mountTimeout: 30000,
    },
  );
  // #endregion parcelloader-props

  type LoaderPhase = 'idle' | 'loading' | 'mounted' | 'destroyed';
  const phaseRef = { current: 'idle' as LoaderPhase };
  function getPhase() {
    return phaseRef.current;
  }
  function setPhase(p: LoaderPhase) {
    phaseRef.current = p;
  }

  const emit = defineEmits<{
    (
      e: 'error',
      payload: { error: Error; attempts: number; package: string; module: string },
    ): void;
  }>();

  const status: Ref<'loading' | 'mounted' | 'error'> = ref('loading');
  const mountError = ref<Error | null>(null);
  const attempts = ref(0);
  const parcelInstance = ref<MountedParcel | null>(null);
  const containerRef = ref<HTMLElement | null>(null);
  const reloading = ref(false);
  const pendingReload = ref<{ type: 'parcel'; url: string } | { type: 'overrides' } | null>(null);

  const mountParcel = inject(MOUNT_PARCEL_KEY, null);
  const parcelRemotes = inject(PARCEL_REMOTES_KEY, null);

  // Track previous props for shallow comparison to avoid unnecessary updates (EDGE-2 fix).
  // prevParcelProps is a plain object snapshot — deeply reactive values within props won't be
  // tracked across updates. This is by design: parcelProps is documented as a plain prop object.
  let prevParcelProps: Record<string, unknown> = {};

  /** Shallow equality check for props objects */
  function shallowEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => Object.is(a[key], b[key]));
  }

  /** Normalize module name to MF convention — devs write 'vehicle-installations', container expects './vehicle-installations' */
  const resolvedModule = computed(() =>
    props.module.startsWith('./') ? props.module : `./${props.module}`,
  );

  /**
   * Dynamic URL resolution — called on every load attempt (not cached at boot).
   * Priority: (1) remoteUrl prop → (2) live overrides → (3) static config (guarded)
   *
   * In production, the container is typically already on window[scope] — installed
   * by the shell from federation config. URL resolution is only needed as fallback.
   * Localhost URLs are blocked in production as a safety net.
   */
  function resolveEffectiveUrl(): string | undefined {
    if (props.remoteUrl) return props.remoteUrl;

    const { overrides } = getActiveOverridesPerScope();
    if (overrides[props.package]) return overrides[props.package];

    const staticUrl = parcelRemotes?.[props.package];
    if (staticUrl) {
      if (isLocalhostUrl(staticUrl) && !isDevMode()) {
        if (process.env?.NODE_ENV === 'development') {
          console.warn(`[ParcelLoader] Blocked localhost URL for ${props.package} in production`);
        }
        return undefined;
      }
      return staticUrl;
    }

    return undefined;
  }

  /**
   * Wait for a federation container to appear on window[scope].
   * Handles the shell case where containers are installed asynchronously during boot
   * after the ParcelLoader component has already mounted.
   *
   * Delegates the presence-wait (event fast-path + polling fallback) to the
   * shared `ensureRemoteReady` primitive with `initShareScope: false` — the
   * loader runs its own per-mount `container.init` guard downstream, so the
   * primitive only resolves presence here, then we read the container back off
   * `window[scope]`.
   */
  async function waitForContainer(
    scope: string,
    timeoutMs: number,
  ): Promise<FederationContainer | undefined> {
    const result = await ensureRemoteReady(scope, {
      timeoutMs,
      pollMs: 100,
      initShareScope: false,
    });
    if (result !== 'ready') return undefined;
    return (window as unknown as Record<string, FederationContainer | undefined>)[scope];
  }

  function dispatchFailedEvent(error: Error): void {
    window.dispatchEvent(
      new CustomEvent('parcel:load-failed', {
        detail: {
          package: props.package,
          module: resolvedModule.value,
          error: error.message,
          attempts: attempts.value,
        },
      }),
    );
  }

  /** Append a `t=<now>` param so a reload bypasses the browser/runtime cache. */
  function withCacheBust(url: string): string {
    return `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
  }

  /**
   * A `.json` entry is a Module Federation manifest: the MF2 runtime auto-detects
   * and consumes it, and it cannot be served by the classic `<script>` window
   * self-registration path. Used to route no-host manifest loads onto the runtime.
   */
  function isManifestEntry(url: string): boolean {
    try {
      return new URL(url, window.location.href).pathname.toLowerCase().endsWith('.json');
    } catch {
      return false;
    }
  }

  async function loadRemoteEntry(url: string, scope: string, cacheBust = false): Promise<void> {
    // CWE-829: refuse to inject a <script> for non-https / non-localhost URLs.
    // The remoteUrl prop flows from caller-controlled sources (env-config,
    // dev-tools overrides), so a tainted URL must fail before script.src
    // assignment — mirror of the shell-side gate in
    // src/utils/module-federation/index.ts.
    // Re-home before the gate so the URL that is validated is the URL that is
    // injected. This script tag carries crossorigin="anonymous", so a CDN URL
    // left on its canonical origin fails closed on any host that is not that
    // origin.
    const entryUrl = rehomeCdnUrlToDocumentOrigin(url);
    if (!isValidEntryUrl(entryUrl)) {
      throw new Error(
        `[parcel-loader] refusing to load remote with non-https / non-localhost url: ${entryUrl}`,
      );
    }
    const scriptUrl = cacheBust ? withCacheBust(entryUrl) : entryUrl;
    const key = `${scope}:${scriptUrl}`;

    const existing = loadingScripts.get(key);
    if (existing) return existing;

    const promise = new Promise<void>((resolve, reject) => {
      if ((window as unknown as Record<string, unknown>)[scope]) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.type = 'text/javascript';
      script.async = true;
      // `crossOrigin = 'anonymous'` to match the shell-side loader
      // (src/utils/module-federation/index.ts). Required for SRI to work
      // and for strict-origin CSP / CORS-restricted CDN configurations.
      script.crossOrigin = 'anonymous';
      script.dataset.parcelScope = scope;

      script.onload = () => {
        if ((window as unknown as Record<string, unknown>)[scope]) {
          window.dispatchEvent(
            new CustomEvent<ContainerLifecycleDetail>(CONTAINER_LOADED_EVENT, {
              detail: { scope, timestamp: Date.now() },
            }),
          );
          resolve();
        } else {
          reject(
            new Error(`Remote scope "${scope}" not found on window after loading ${scriptUrl}`),
          );
        }
      };

      script.onerror = () => {
        script.remove();
        reject(new Error(`Failed to load remote entry from ${scriptUrl}`));
      };

      document.head.appendChild(script);
    });

    loadingScripts.set(key, promise);
    try {
      await promise;
      // Success path: leave the entry in `loadingScripts` so concurrent
      // callers reuse the resolved promise. Removing on success defeated
      // dedup — a second caller arriving moments later would append a
      // duplicate `<script>` tag for the same URL.
    } catch (err) {
      // Failure path: pop the entry so a retry can attempt a fresh load.
      // The previous `finally` block ran on success too, which is why a
      // transient error + immediate retry sometimes inserted a second
      // `<script>` for the same URL while the original was still in flight.
      loadingScripts.delete(key);
      throw err;
    }
  }

  /** Race promise against timeout — returns original promise result or throws timeout error */
  async function withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    message: string,
  ): Promise<T> {
    if (timeoutMs <= 0) return promise;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  }

  /**
   * Shared mount tail for both load paths (classic factory + runtime loadRemote):
   * await async webpack modules, unwrap the lifecycle, validate its shape with
   * developer diagnostics, then mount it through single-spa with the configured
   * timeout. Throwing here feeds loadAndMount's retry/backoff.
   */
  async function mountLifecycleModule(rawModule: unknown, retryCount: number): Promise<void> {
    let moduleExports = rawModule as Record<string, unknown>;

    // Handle async webpack modules — when a module uses top-level `await`,
    // webpack wraps it with __webpack_require__.a and the factory returns a
    // thenable whose export bindings are uninitialized until the async init
    // completes; loadRemote can likewise hand back a thenable. Awaiting it
    // ensures exports are populated before validation.
    if (typeof (moduleExports as { then?: unknown }).then === 'function') {
      moduleExports = await (moduleExports as unknown as Promise<Record<string, unknown>>);
    }

    // Unwrap default export if present (handles both `export default` and named exports)
    const lifecycle = ((moduleExports as { default?: ParcelConfig }).default ??
      moduleExports) as ParcelConfig;

    // Runtime validation of lifecycle shape with developer-friendly diagnostics
    if (typeof lifecycle?.mount !== 'function') {
      const exportKeys = Object.keys(moduleExports).filter((k) => k !== '__esModule');
      const hints: string[] = [];

      if (exportKeys.length === 0) {
        hints.push(
          'The module returned no exports. Check for runtime errors during module evaluation.',
        );
      } else if (!exportKeys.includes('mount') && !exportKeys.includes('default')) {
        hints.push(`Found exports: [${exportKeys.join(', ')}] — none provide a parcel lifecycle.`);
        hints.push(
          'Use defineParcel() from @abax/platform to create a valid lifecycle, or export { bootstrap, mount, unmount }.',
        );
      }

      throw new Error(
        `Remote module "${resolvedModule.value}" did not return a valid parcel lifecycle (missing mount).` +
          (hints.length > 0 ? '\n  ' + hints.join('\n  ') : ''),
      );
    }

    if (!containerRef.value) throw new Error('Container element not available');

    // mountParcel is guaranteed present — loadAndMount's guard returns early otherwise.
    parcelInstance.value = mountParcel!(lifecycle, {
      domElement: containerRef.value,
      ...props.parcelProps,
    });

    await withTimeout(
      parcelInstance.value.mountPromise,
      props.mountTimeout,
      `Parcel mount timed out after ${props.mountTimeout}ms for ${props.package}/${resolvedModule.value}`,
    );
    if (getPhase() === 'destroyed') return;

    status.value = 'mounted';
    attempts.value = retryCount + 1;
    setPhase('mounted');
  }

  async function loadAndMount(
    retryCount = 0,
    _fromReload = false,
    entryOverride?: string,
  ): Promise<void> {
    if (getPhase() === 'loading' || getPhase() === 'destroyed') return;
    if (reloading.value && !_fromReload) return;
    setPhase('loading');

    status.value = 'loading';
    mountError.value = null;

    if (!mountParcel) {
      status.value = 'error';
      mountError.value = new Error(
        'mountParcel not provided — ensure ParcelLoader is used within a shell or dev-shell context',
      );
      attempts.value = 1;
      dispatchFailedEvent(mountError.value);
      setPhase('idle');
      return;
    }

    try {
      const host = getHostFederationInstance();
      // Resolution: reload override → explicit prop → live overrides → static config
      //
      // Re-homed at the point of resolution rather than at each use, because both
      // paths below consume this string: the runtime registers it as a remote entry
      // and the classic path injects it as a script. A CDN URL left on its canonical
      // origin fails closed on any host that is not that origin, and the runtime
      // fetch has no crossorigin attribute to relax. Idempotent, and it preserves the
      // pathname, so the `.json` manifest test below is unaffected.
      const resolvedEntry = entryOverride ?? resolveEffectiveUrl();
      const entry = resolvedEntry ? rehomeCdnUrlToDocumentOrigin(resolvedEntry) : resolvedEntry;

      // The rules that pick the protocol live with resolveLoadStrategy.
      const strategy = resolveLoadStrategy({
        entry,
        hasHost: !!host,
        manifestEntry: !!entry && isManifestEntry(entry),
        classicContainer: !!(window as unknown as Record<string, unknown>)[props.package],
        runtimeRegistered: runtimeRegisteredScopes.has(props.package),
        hostKnowsRemote: !!host && instanceHasRemote(host, props.package),
        loaderRegisteredOnHost: !!host && isLoaderRegistered(host, props.package),
      });

      if (strategy === 'runtime') {
        const instance = host ?? (await getStandaloneFederationInstance());

        if (entry) {
          // CWE-829: gate the resolved entry before it reaches the runtime loader,
          // mirroring loadRemoteEntry()'s classic gate (moved earlier on this path).
          if (!isValidEntryUrl(entry)) {
            throw new Error(
              `[parcel-loader] refusing to load remote with non-https / non-localhost url: ${entry}`,
            );
          }
          // force:true evicts a registered remote: its snapshot, its loaded container
          // and any load in flight, on every consumer sharing the instance. Only a
          // changed entry (hot reload, an override) is worth that; a repeat of the
          // same entry, such as a second loader for the same package, registers nothing.
          // The mark records what the loader itself registered: a no-op on an entry
          // the host bootstrap declared leaves that remote the host's own.
          const registered = instance.options?.remotes?.find((r) => r.name === props.package);
          if (!registered) {
            instance.registerRemotes([{ name: props.package, entry }]);
            markLoaderRegistered(instance, props.package);
          } else if (registered.entry !== entry) {
            instance.registerRemotes([{ name: props.package, entry }], { force: true });
            markLoaderRegistered(instance, props.package);
          }
        }
        if (getPhase() === 'destroyed') return;

        // loadRemote returns the RESOLVED module (no factory() step), auto-detecting
        // a `.json` manifest vs a `.js` remoteEntry. The expose id drops the `./`.
        const exposeId = `${props.package}/${resolvedModule.value.replace(/^\.\//, '')}`;
        const mod = await instance.loadRemote<Record<string, unknown>>(exposeId);
        if (getPhase() === 'destroyed') return;
        if (mod == null) {
          throw new Error(`Remote module "${exposeId}" resolved to null via loadRemote`);
        }

        await mountLifecycleModule(mod, retryCount);
      } else {
        // Classic window[scope] container protocol (transition fallback).
        // Load remote entry script if container isn't on window yet.
        // Resolution: explicit prop → live overrides → static config → wait for host → fail (R-03, R-36)
        let urlWasAttempted = false;
        if (!(window as unknown as Record<string, unknown>)[props.package]) {
          if (entry) {
            urlWasAttempted = true;
            await loadRemoteEntry(entry, props.package);
            if (getPhase() === 'destroyed') return;
          }
        }

        let container = (window as unknown as Record<string, FederationContainer | undefined>)[
          props.package
        ];

        // When no URL was available and container isn't on window yet, wait for the host
        // (e.g. shell) to install it. This handles the boot timing gap where ParcelLoader
        // mounts before the host finishes loading federation packages onto window[scope].
        if (!container && !urlWasAttempted) {
          container = await waitForContainer(props.package, props.mountTimeout);
          if (getPhase() === 'destroyed') return;
        }

        if (!container) throw new Error(`Remote container "${props.package}" not found on window`);

        // Initialize shared scope if container has init() and webpack share scopes exist (EDGE-4 fix)
        if (typeof container.init === 'function') {
          try {
            const webpackShareScopes = (window as unknown as Record<string, unknown>)
              .__webpack_share_scopes__ as Record<string, unknown> | undefined;
            if (webpackShareScopes?.default) {
              await container.init(webpackShareScopes.default as Record<string, unknown>);
            } else if (process.env?.NODE_ENV === 'development') {
              console.warn(
                `[ParcelLoader] window.__webpack_share_scopes__ not found for ${props.package}. ` +
                  'Ensure the host app calls __webpack_init_sharing__("default") and exposes the scope on window.',
              );
            }
          } catch (initErr) {
            // Expected when init was already called by another consumer (single-spa app or another parcel).
            // Module Federation containers can only be initialized once — subsequent calls throw.
          }
        }
        if (getPhase() === 'destroyed') return;

        const factory = await container.get(resolvedModule.value);
        if (getPhase() === 'destroyed') return;
        if (typeof factory !== 'function')
          throw new Error(
            `Remote module "${resolvedModule.value}" did not return a factory function`,
          );

        await mountLifecycleModule(factory(), retryCount);
      }
    } catch (error) {
      if (getPhase() === 'destroyed') return;

      const err = error instanceof Error ? error : new Error(String(error));
      if (retryCount < props.maxRetries) {
        const delay = Math.min(1000 * 2 ** retryCount, 5000);
        await new Promise((r) => setTimeout(r, delay));
        if (getPhase() === 'destroyed' || getPhase() === 'mounted') return;
        if (reloading.value && !_fromReload) return;
        setPhase('idle');
        return await loadAndMount(retryCount + 1, _fromReload, entryOverride);
      }

      attempts.value = retryCount + 1;
      mountError.value = err;
      status.value = 'error';
      setPhase('idle');
      console.error(
        `[ParcelLoader] Failed to load parcel ${props.package}/${resolvedModule.value} after ${attempts.value} attempts:`,
        err,
      );
      dispatchFailedEvent(err);
      emit('error', {
        error: err,
        attempts: attempts.value,
        package: props.package,
        module: resolvedModule.value,
      });
    }
  }

  function retry(): void {
    if (getPhase() === 'destroyed') return;
    loadAndMount(0);
  }

  /**
   * Shared reload core — unmounts, invalidates, optionally loads a specific entry URL,
   * then remounts. Manages the reloading guard and drains the pending-reload queue.
   *
   * When opts.entryUrl is provided, loads that URL with cache-bust before remounting.
   * When omitted, lets loadAndMount resolve the URL dynamically (via resolveEffectiveUrl).
   */
  async function doReload(opts: {
    entryUrl?: string;
    queueEntry: typeof pendingReload.value;
    label: string;
  }): Promise<void> {
    if (getPhase() === 'destroyed') return;
    if (reloading.value) {
      // Queue — will be processed after current reload completes
      pendingReload.value = opts.queueEntry;
      return;
    }
    reloading.value = true;

    try {
      // Step 1: Unmount current parcel if mounted
      if (parcelInstance.value) {
        try {
          const currentStatus = parcelInstance.value.getStatus();
          if (currentStatus === ParcelStatus.MOUNTED) {
            await parcelInstance.value.unmount();
          }
        } catch (err) {
          console.warn(`[ParcelLoader] Unmount during ${opts.label} failed (non-critical):`, err);
        }
        parcelInstance.value = null;
      }

      // Step 2: Invalidate the container (delete window[scope] + remove script tag)
      invalidateContainer(props.package);

      // Step 3: reload the code for the new entry.
      // Runtime path: hand a cache-busted entry to loadAndMount so its
      // registerRemotes(force) re-fetches instead of returning the cached
      // snapshot — no <script> inject. Classic path: script-inject with cache-bust
      // so the new container self-registers on window[scope] before remount.
      // Same predicate as loadAndMount's useRuntime: a `.json` manifest goes to
      // the runtime even with no host, since a <script> cannot serve it.
      let runtimeReloadEntry: string | undefined;
      if (opts.entryUrl) {
        if (getHostFederationInstance() || isManifestEntry(opts.entryUrl)) {
          runtimeReloadEntry = withCacheBust(opts.entryUrl);
        } else {
          await loadRemoteEntry(opts.entryUrl, props.package, true);
          if (getPhase() === 'destroyed') return;
        }
      }

      // Step 4: Reset phase and remount with same props.
      // When no entry was provided, resolveEffectiveUrl() dynamically resolves the URL.
      setPhase('idle');
      await loadAndMount(0, true, runtimeReloadEntry);
    } catch (err) {
      console.error(`[ParcelLoader] ${opts.label} failed:`, err);
      if (status.value !== 'error') {
        mountError.value = err instanceof Error ? err : new Error(String(err));
        status.value = 'error';
        setPhase('idle');
      }
    } finally {
      reloading.value = false;
      const pending = pendingReload.value;
      pendingReload.value = null;
      if (pending) {
        if (pending.type === 'parcel') reloadParcel(pending.url);
        else reloadFromCurrentOverrides();
      }
    }
  }

  async function reloadParcel(entryUrl: string): Promise<void> {
    return doReload({
      entryUrl,
      queueEntry: { type: 'parcel', url: entryUrl },
      label: 'Hot reload',
    });
  }

  /**
   * Reload parcel by re-resolving URL dynamically from current overrides.
   * Used when an override is disabled — falls back to static config URL.
   */
  async function reloadFromCurrentOverrides(): Promise<void> {
    return doReload({
      queueEntry: { type: 'overrides' },
      label: 'Override reload',
    });
  }

  function handleContainerInvalidated(event: Event): void {
    const detail = (event as CustomEvent<ContainerLifecycleDetail>).detail;
    if (detail?.scope !== props.package) return;
    if (reloading.value || getPhase() === 'destroyed') return;
    console.info(
      `[ParcelLoader] Override invalidated for ${props.package}/${resolvedModule.value}, reloading`,
    );
    reloadFromCurrentOverrides();
  }

  function handleContainerChanged(event: Event): void {
    const detail = (event as CustomEvent<{ scope: string; entryUrl: string }>).detail;
    if (detail?.scope !== props.package) return;
    if (isValidDevEntryUrl(detail.entryUrl)) {
      // Localhost dev server → direct hot-reload with cache-bust
      console.info(`[ParcelLoader] Hot-reloading ${props.package}/${resolvedModule.value}`);
      reloadParcel(detail.entryUrl);
    } else {
      // Non-localhost (CDN, staging) → re-resolve from override layers
      console.info(
        `[ParcelLoader] Reloading ${props.package}/${resolvedModule.value} from overrides`,
      );
      reloadFromCurrentOverrides();
    }
  }

  onMounted(() => {
    loadAndMount(0);
    window.addEventListener(CONTAINER_CHANGED_EVENT, handleContainerChanged);
    window.addEventListener(CONTAINER_INVALIDATED_EVENT, handleContainerInvalidated);
  });

  onBeforeUnmount(async () => {
    setPhase('destroyed');
    window.removeEventListener(CONTAINER_CHANGED_EVENT, handleContainerChanged);
    window.removeEventListener(CONTAINER_INVALIDATED_EVENT, handleContainerInvalidated);
    if (!parcelInstance.value) return;
    try {
      const status = parcelInstance.value.getStatus();
      // Skip unmount for broken parcels — they're already in an error state
      if (isParcelBroken(status)) {
        console.warn(`[ParcelLoader] Parcel in broken state (${status}), skipping unmount`);
        return;
      }
      if (status === ParcelStatus.MOUNTED) {
        await parcelInstance.value.unmount();
      }
    } catch (err) {
      console.warn('[ParcelLoader] Unmount error (non-critical):', err);
    }
  });

  // Forward prop changes to mounted parcel
  watch(
    () => props.parcelProps,
    (newProps) => {
      if (getPhase() === 'destroyed') return;

      // Skip if props haven't actually changed (shallow comparison) — EDGE-2 fix
      const propsObj = newProps ?? {};
      if (shallowEqual(propsObj, prevParcelProps)) return;
      prevParcelProps = { ...propsObj };

      const instance = parcelInstance.value;
      // Only call update if: instance exists, update is a function, and parcel is mounted (BUG-2 fix)
      if (
        instance &&
        typeof instance.update === 'function' &&
        instance.getStatus() === ParcelStatus.MOUNTED
      ) {
        instance.update(newProps).catch((err: unknown) => {
          console.warn('[ParcelLoader] Parcel update failed:', err);
          // Emit error event so parent can handle update failures (ISS-5 fix)
          emit('error', {
            error: err instanceof Error ? err : new Error(String(err)),
            attempts: attempts.value,
            package: props.package,
            module: resolvedModule.value,
          });
        });
      }
    },
  );

  // Dispatch status change events for dev-shell parcel registry.
  // nextTick ensures data-parcel-status DOM attribute is patched before event fires.
  watch(status, async (newStatus) => {
    await nextTick();
    window.dispatchEvent(
      new CustomEvent(STATUS_CHANGED_EVENT, {
        detail: {
          scope: props.package,
          module: resolvedModule.value,
          status: newStatus,
        },
      }),
    );
  });
</script>

<template>
  <slot v-if="status === 'loading' && !silent" name="fallback" />
  <slot
    v-else-if="status === 'error' && !silent"
    name="error"
    :error="mountError"
    :retry="retry"
    :attempts="attempts"
    :package="props.package"
    :module="resolvedModule"
  />
  <div
    ref="containerRef"
    v-show="status === 'mounted'"
    :data-parcel-scope="props.package"
    :data-parcel-module="resolvedModule"
    :data-parcel-status="status"
  />
</template>
